import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { toast } from "sonner";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/reset-password")({
  head: () =>
    buildSeo({
      path: "/reset-password",
      title: "Set new password — Reality Twin",
      description:
        "Set a new password for your Reality Twin account. The reset link is single-use and expires shortly after it is issued.",
      noindex: true,
    }),
  component: Page,
});

function Page() {
  const t = useHydratedTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Supabase parses recovery token from URL hash and fires PASSWORD_RECOVERY
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data: s }) => {
      if (s.session) setReady(true);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 8) return toast.error(t("routes.auth.tooShort"));
    if (password !== confirm) return toast.error(t("routes.auth.mismatch"));
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) return toast.error(error.message);
    toast.success(t("routes.auth.updated"));
    navigate({ to: "/dashboard" });
  }

  return (
    <Layout>
      <section className="mx-auto max-w-md px-6 py-24">
        <h1 className="font-display text-4xl font-semibold text-gradient">
          {t("routes.auth.resetTitle")}
        </h1>
        {!ready ? (
          <p className="mt-6 text-sm text-muted-foreground">{t("routes.auth.waiting")}</p>
        ) : (
          <form onSubmit={submit} className="mt-8 glass-strong rounded-2xl p-6 space-y-4">
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1.5">
                {t("routes.auth.newPassword")}
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none"
              />
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono mb-1.5">
                {t("routes.auth.confirmLabel")}
              </div>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-xl bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none"
              />
            </label>
            <button
              disabled={loading}
              type="submit"
              className="w-full rounded-full brand-gradient text-black py-2.5 text-sm font-medium disabled:opacity-50"
            >
              {loading ? t("routes.auth.updating") : t("routes.auth.update")}
            </button>
          </form>
        )}
      </section>
    </Layout>
  );
}
